import MaskToggle from './MaskToggle';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Navigation, X } from 'lucide-react';
import ToolDrawer from './ToolDrawer';
import GhostCamera from './GhostCamera';
import { DrawBar } from './PipePanel';
import { VoiceRecorder } from './VoiceMemo';
import { ThumbBar } from './GloveMode';
import QuickBar from './QuickBar';
import VoiceCmd from './VoiceCmd';
import { useCamDir } from '../lib/photoDir';

function MemoBubble({ t, net, fitId, sticky, onClose, onHold }) {
  const fit = net.fittings.find((f) => f.id === fitId);
  useEffect(() => {
    if (sticky) return undefined;
    const id = setTimeout(onClose, 8000);
    return () => clearTimeout(id);
  }, [fitId, sticky, onClose]);
  if (!fit) return null;
  const done = (m) => { net.updateFitting(fit.id, (x) => ({ memos: [...(x.memos || []), m] })); toast.success(t.memoSaved); onClose(); };
  return (
    <div className="fs-memo mask" data-testid="fs-memo-bubble" onPointerDown={() => !sticky && onHold()}>
      <span className="fs-memo-name" data-testid="fs-memo-target">{t.memoBubble}: <b>{fit.name}</b></span>
      <input className="fs-serial" data-testid="fs-serial-input" placeholder={t.serial} value={fit.serial || ''} onFocus={onHold} onChange={(e) => net.updateFitting(fit.id, { serial: e.target.value })} />
      <VoiceRecorder t={t} compact testid="fs-memo-record-btn" onDone={done} />
      <button className="icon-btn" data-testid="fs-memo-close-btn" onClick={onClose}><X size={15} /></button>
    </div>
  );
}

const camOk = () => !!navigator.mediaDevices?.getUserMedia && document.featurePolicy?.allowsFeature?.('camera') !== false;

export default function FieldMode({ t, net, pits, mapRef, onGps, onCapture, follow, setFollow, camReq, onMainPhoto, onGallery, onLogger, onFitAll }) {
  const [cam, setCam] = useState(null);
  const fbRef = useRef(null);
  const fallback = (mode) => { const el = fbRef.current; el.accept = mode === 'video' ? 'video/*' : 'image/*'; el.click(); };
  const openCam = (mode, xray = false) => (camOk() ? setCam({ mode, xray }) : fallback(mode));
  useEffect(() => { if (camReq) openCam(camReq.mode, camReq.xray); }, [camReq]); // eslint-disable-line react-hooks/exhaustive-deps
  const [memo, setMemo] = useState(null);
  const camDir = useCamDir(!!cam);
  useEffect(() => { if (net.lastFit) setMemo({ id: net.lastFit.id, sticky: false }); }, [net.lastFit]);
  const shot = async (file) => {
    const c = mapRef.current.getCenter();
    if (await onCapture(file, { lat: c.lat, lon: c.lng, ...(camDir.current || {}) })) toast.success(t.camSaved);
  };
  const memoLast = () => {
    const f = net.fittings.find((x) => x.id === net.lastFit?.id) || net.fittings[net.fittings.length - 1];
    if (!f) return toast.message(t.memoNone);
    setMemo({ id: f.id, sticky: true });
  };
  const closeMemo = useCallback(() => setMemo(null), []);
  const holdMemo = useCallback(() => setMemo((m) => m && { ...m, sticky: true }), []);
  return (
    <>
      <input ref={fbRef} type="file" accept="image/*" capture="environment" hidden data-testid="fs-camera-fallback-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) shot(f); }} />
      {cam && <GhostCamera key={cam.mode + cam.xray} t={t} mode={cam.mode} xray={cam.xray} onShot={shot} onClose={() => setCam(null)} onFail={() => { setCam(null); fallback(cam.mode); }} />}
      {!cam && <ToolDrawer t={t} net={net} pits={pits} onCamera={(m) => openCam(m)} onGps={onGps} onMemo={memoLast} onMainPhoto={onMainPhoto} onGallery={onGallery} onLogger={onLogger} onFitAll={onFitAll} />}
      {!cam && (
        <button className={`chip fs-follow ${follow ? 'on' : ''}`} data-testid="fs-follow-btn" onClick={() => setFollow(!follow)}>
          <Navigation size={14} /> {follow ? t.gpsFollow : t.gpsFollowOff}
        </button>
      )}
      {!cam && memo && <MemoBubble t={t} net={net} fitId={memo.id} sticky={memo.sticky} onClose={closeMemo} onHold={holdMemo} />}
      {!cam && <ThumbBar t={t} net={net} />}
      {!cam && <QuickBar t={t} net={net} />}
      {!cam && <VoiceCmd t={t} net={net} />}
      {!cam && net.draft && <div className="fs-drawbar mask" data-testid="fs-draw-bar"><MaskToggle t={t} id="fs-drawbar" /><DrawBar t={t} net={net} pits={pits} /></div>}
    </>
  );
}
